import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { Controller, useForm } from "react-hook-form";

import { confirmSignIn, signIn, signInWithRedirect } from "@aws-amplify/auth";
import { useState } from "react";

import Orb from "@/assets/orb.mp4";

import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { FormField } from "./ui/form";
import InputError from "./ui/InputError";

interface LoginDTO {
  username: string;
  otp: string;
}

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const {
    handleSubmit,
    control,
    setError,
    formState: { isValid },
    setValue,
    getValues,
  } = useForm<LoginDTO>({
    defaultValues: {
      username: "",
      otp: "",
    },
  });

  const submitLogin = async (data: LoginDTO) => {
    setLoading(true);
    try {
      if (sent) {
        await confirmSignIn({ challengeResponse: data.otp });
      } else {
        await signIn({
          username: data.username,
          options: {
            authFlowType: "CUSTOM_WITHOUT_SRP",
          },
        });
        setSent(true);
      }
    } catch (error: unknown) {
      console.error("🚀 Login ~ error:", error);
      if (error) {
        let msg = (error as Error)?.message;
        if (msg === "Incorrect username or password.")
          msg = "Valida los datos ingresados";

        if (sent)
          setError("otp", {
            type: "custom",
            message: msg ?? "Error, intenta nuevamente",
          });
        else
          setError("username", {
            type: "custom",
            message: msg ?? "Error, intenta nuevamente",
          });
      }
    } finally {
      setLoading(false);
    }
  };
  const googleLogin = async () => {
    try {
      await signInWithRedirect({ provider: "Google" });
    } catch (error) {
      console.error("🚀 Login ~ error Google:", error);
      if (error) {
        const msg = (error as Error)?.message;

        setError("username", {
          type: "custom",
          message: msg ?? "Error, intenta nuevamente",
        });
      }
    }
  };

  const handleBackToLogin = () => {
    setSent(false);
    setValue("username", "");
    setValue("otp", "");
  };

  return (
    <div
      className={cn("flex flex-col gap-6", className)}
      {...props}
      data-testid="login-form"
    >
      <Card className="overflow-hidden border-b border-none shadow-none">
        <CardContent className="grid p-0 md:grid-cols-2">
          <form className="p-6 md:p-8" onSubmit={handleSubmit(submitLogin)}>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center text-center">
                <h1 className="text-2xl font-bold">Bienvenido</h1>
                <p className="text-balance text-muted-foreground">
                  Ingresa con tu teléfono
                </p>
              </div>
              {sent ? (
                <FormField
                  control={control}
                  name="otp"
                  rules={{ maxLength: 6, minLength: 6, required: sent }}
                  render={({ field, formState }) => {
                    const errMsg: string | undefined =
                      formState?.errors?.otp?.message;
                    return (
                      <>
                        <div className="flex justify-center">
                          <InputOTP maxLength={6} {...field} data-testid="otp">
                            <InputOTPGroup>
                              <InputOTPSlot index={0} />
                              <InputOTPSlot index={1} />
                              <InputOTPSlot index={2} />
                            </InputOTPGroup>
                            <InputOTPSeparator />
                            <InputOTPGroup>
                              <InputOTPSlot index={3} />
                              <InputOTPSlot index={4} />
                              <InputOTPSlot index={5} />
                            </InputOTPGroup>
                          </InputOTP>
                        </div>
                        <div className="text-center">
                          {errMsg && (
                            <>
                              <InputError errMsg={errMsg} />{" "}
                              <span className="text-xs">
                                {getValues("username")}
                              </span>
                            </>
                          )}
                        </div>
                      </>
                    );
                  }}
                />
              ) : (
                <div className="grid gap-2">
                  <Label htmlFor="email">Teléfono</Label>

                  <Controller
                    control={control}
                    name="username"
                    disabled={loading}
                    render={({ field, formState }) => {
                      const errMsg: string | undefined =
                        formState?.errors?.username?.message;
                      return (
                        <>
                          <Input
                            placeholder="+5255555555"
                            required
                            data-testid="username"
                            {...field}
                          />
                          {errMsg && <InputError errMsg={errMsg} />}
                        </>
                      );
                    }}
                  />
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={loading || !isValid}
                data-testid="submit-btn"
              >
                {loading ? "Cargando..." : "Inicia sesión"}
              </Button>
              <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
                <span className="relative z-10 bg-background px-2 text-muted-foreground">
                  O continúa con
                </span>
              </div>
              <div className="grid grid-cols-1 gap-4">
                <Button
                  variant="outline"
                  className="w-full"
                  type="button"
                  onClick={googleLogin}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path
                      d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                      fill="currentColor"
                    />
                  </svg>
                  <span className="sr-only">Inicia con Google</span>
                </Button>
              </div>
              <div className="text-center text-sm">
                ¿No tienes una cuenta?{" "}
                <a href="#" className="underline underline-offset-4">
                  Registrate
                </a>
              </div>
              {sent && (
                <div className="text-md text-center">
                  <Button
                    data-testid="back-btn"
                    onClick={handleBackToLogin}
                    variant={"secondary"}
                  >
                    Volver a login
                  </Button>
                </div>
              )}
            </div>
          </form>
          <div className="relative hidden items-center justify-center md:flex">
            <div className="object-cover">
              <video autoPlay muted loop>
                <source src={Orb} type="video/mp4" />
              </video>
            </div>
          </div>
        </CardContent>
      </Card>
      <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary">
        Al continuar estás aceptando nuestros{" "}
        <a href="#">Términos y Condiciones</a> y nuestra{" "}
        <a href="#">Política de Privacidad</a>.
      </div>
    </div>
  );
}
